import React, { useState } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { SidebarMenuGroupProps } from '../../types';
import { SidebarMenuItem, SidebarDivider } from '../atoms';

/**
 * Groups menu rows under an optional section title (Figma section headers).
 * With `collapsible`, the header toggles the item list (chevron up/down).
 * When `collapsed`, titled groups render a plain divider (unless skipped) and items only.
 */
export const SidebarMenuGroup: React.FC<SidebarMenuGroupProps> = ({
  title,
  items,
  collapsible = false,
  defaultExpanded = true,
  collapsed = false,
  skipLeadingPlainDivider = false,
  className,
}) => {
  const [sectionOpen, setSectionOpen] = useState(defaultExpanded);

  const handleToggleSection = () => {
    setSectionOpen((open) => !open);
  };

  if (collapsed) {
    if (!title) {
      return (
        <div className={cn('ui-flex ui-w-full ui-flex-col ui-gap-4', className)}>
          {items.map((item) => (
            <SidebarMenuItem key={item.id} {...item} />
          ))}
        </div>
      );
    }

    if (skipLeadingPlainDivider) {
      return (
        <div className={cn('ui-flex ui-w-full ui-flex-col ui-gap-4', className)}>
          {items.map((item) => (
            <SidebarMenuItem key={item.id} {...item} />
          ))}
        </div>
      );
    }

    return (
      <div className={cn('ui-flex ui-w-full ui-flex-col', className)}>
        <SidebarDivider />
        <div className="ui-flex ui-w-full ui-flex-col ui-gap-4">
          {items.map((item) => (
            <SidebarMenuItem key={item.id} {...item} />
          ))}
        </div>
      </div>
    );
  }

  if (!title) {
    return (
      <div className={cn('ui-flex ui-w-full ui-flex-col ui-gap-4', className)}>
        {items.map((item) => (
          <SidebarMenuItem key={item.id} {...item} />
        ))}
      </div>
    );
  }

  if (!collapsible) {
    return (
      <div className={cn('ui-flex ui-w-full ui-flex-col', className)}>
        <SidebarDivider withText text={title} />
        <div className="ui-flex ui-w-full ui-flex-col ui-gap-4">
          {items.map((item) => (
            <SidebarMenuItem key={item.id} {...item} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('ui-flex ui-w-full ui-flex-col', className)}>
      {skipLeadingPlainDivider ? null : <SidebarDivider />}
      <button
        type="button"
        onClick={handleToggleSection}
        className={cn(
          'ui-flex ui-h-32 ui-min-h-32 ui-w-full ui-cursor-pointer ui-items-center ui-gap-10',
          'ui-rounded-control-sm ui-border-0 ui-bg-transparent ui-px-8 ui-py-0 ui-text-left ui-outline-none',
          'hover:ui-bg-fill-hover',
          'focus-visible:ui-ring-brand focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-offset-2'
        )}
        aria-expanded={sectionOpen}
      >
        <Icon
          name={sectionOpen ? faChevronUp : faChevronDown}
          size="xs"
          color="tertiary"
          className="ui-shrink-0"
        />
        <span
          className={cn(
            'ui-min-w-0 ui-flex-1 ui-truncate',
            'ui-text-body-xs-regular ui-font-body-xs-regular',
            'ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary'
          )}
        >
          {title}
        </span>
      </button>
      {sectionOpen ? (
        <div className="ui-flex ui-w-full ui-flex-col ui-gap-4">
          {items.map((item) => (
            <SidebarMenuItem key={item.id} {...item} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
